import { useQueryClient } from '@tanstack/react-query';

import type { Session } from '#core/lib/expr/session/Session.ts';

import { createChatSession } from './createChatSession.ts';
import { useThreadKeyedResource } from '#plugin-chat/hooks/useThreadKeyedResource.ts';

/**
 * Hook returning a TanStack-Query-backed `Session` scoped to a
 * conversation thread id. One session per thread id: switching threads
 * gives that thread its own cache prefix.
 *
 * The session is keyed to `threadId` via {@link useThreadKeyedResource}
 * — a `useState` + render-time reset (NOT `useMemo`; see that hook for
 * the React 19 + React Compiler rationale). Within a single thread id
 * the session is stable across re-renders so its `fetch` I/O cache
 * persists; a thread switch creates that thread's session during render.
 *
 * The session carries no binding state. Cross-turn dataset bindings are
 * a pure projection of the active message branch, re-derived on demand
 * by the per-tool resolver (`useBindingScope` / `useThreadScopedEval`),
 * so reload, thread-switch, regenerate, edit and branch-switch all stay
 * consistent with what the user sees with nothing to hydrate.
 *
 * Mount with React 19's context-as-provider syntax:
 *
 * ```tsx
 * function Chat({ threadId, children }) {
 *   const session = useChatSession(threadId);
 *   return (
 *     <SessionContext value={session}>
 *       {children}
 *     </SessionContext>
 *   );
 * }
 * ```
 */
export function useChatSession(threadId: string): Session {
  const queryClient = useQueryClient();

  return useThreadKeyedResource(threadId, () =>
    createChatSession({
      queryClient,
      sessionId: threadId,
    }),
  );
}
